// src/components/EnquiryForm.jsx

import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { submitEnquiry } from "../api";
import { ENQUIRY_SUBJECTS, SITE } from "../content";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  subjectRef: "",
  message: "",
};

// ---------------------------------------------------------------------------
// Turnstile is rendered explicitly rather than by the implicit class scan, so
// React controls when the widget mounts and it can be reset after a failed
// submit - tokens are single use.
// ---------------------------------------------------------------------------
function useTurnstile(onToken) {
  const boxRef = useRef(null);
  const idRef = useRef(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;

    const render = () => {
      if (cancelled || !boxRef.current || idRef.current !== null) return;
      idRef.current = window.turnstile.render(boxRef.current, {
        sitekey: SITE_KEY,
        theme: "auto",
        callback: onToken,
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
    };

    // The script is loaded async in index.html, so it may not be ready yet.
    if (window.turnstile) {
      render();
      return () => {
        cancelled = true;
      };
    }

    const t = setInterval(() => {
      if (window.turnstile) {
        clearInterval(t);
        render();
      }
    }, 120);

    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [onToken]);

  const reset = useCallback(() => {
    if (idRef.current !== null && window.turnstile) {
      window.turnstile.reset(idRef.current);
    }
    onToken(null);
  }, [onToken]);

  return { boxRef, reset };
}

// ---------------------------------------------------------------------------
export default function EnquiryForm({ brief }) {
  const [form, setForm] = useState(EMPTY);
  const [token, setToken] = useState(null);
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");

  const onToken = useCallback((t) => setToken(t), []);
  const { boxRef, reset } = useTurnstile(onToken);

  // A brief handed over by the sizing tool fills the message and the subject.
  // Anything the visitor has already typed is kept above it.
  useEffect(() => {
    if (!brief) return;
    setState("idle");
    setForm((f) => ({
      ...f,
      subjectRef: brief.subjectRef || f.subjectRef,
      message: f.message.trim()
        ? `${f.message.trim()}\n\n${brief.message}`
        : brief.message,
    }));
  }, [brief]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email.trim() && !form.phone.trim()) {
      setError("Please give either an email address or a phone number.");
      return;
    }
    if (!token) {
      setError("Please complete the security check below.");
      return;
    }

    setState("sending");
    try {
      const subject = ENQUIRY_SUBJECTS.find((s) => s.ref === form.subjectRef);
      await submitEnquiry({
        vertical: "maintenance",
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        company: form.company.trim() || undefined,
        message: form.message.trim() || undefined,
        pagePath: window.location.pathname,
        subjectRef: form.subjectRef || undefined,
        subjectLabel: subject ? subject.label : undefined,
        turnstileToken: token,
      });
      setState("done");
      setForm(EMPTY);
    } catch (err) {
      setError(err.message);
      setState("error");
      // The token is spent whether or not the request succeeded.
      reset();
    }
  };

  return (
    <section id="enquire" className="border-t border-line py-20 sm:py-28">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brooke-red">
            Get in touch
          </p>
          <h2 className="mt-3 max-w-[16ch] font-arch text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
            Tell us what needs attention
          </h2>
          <p className="mt-5 max-w-[46ch] text-[15px] leading-relaxed text-muted">
            Send the details and the office will come back to you. If it is
            urgent, calling is faster.
          </p>

          <dl className="mt-10 space-y-5 border-t border-line pt-8">
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
                Phone
              </dt>
              <dd className="mt-1">
                <a
                  href={`tel:${SITE.phone}`}
                  className="font-arch text-xl font-semibold text-ink transition hover:text-brooke-red"
                >
                  {SITE.phoneLabel}
                </a>
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
                Workshop
              </dt>
              <dd className="mt-1 text-[15px] leading-relaxed text-muted">
                {SITE.address.street}
                <br />
                {SITE.address.locality}, {SITE.address.region} {SITE.address.postal}
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
                Hours
              </dt>
              <dd className="mt-1 text-[15px] text-muted">
                Monday to Friday, 08:00 to 17:00
              </dd>
            </div>
          </dl>
        </div>

        {/* --- the form --------------------------------------------------- */}
        <div className="rounded-2xl border border-line bg-surface2 p-6 sm:p-9">
          <AnimatePresence mode="wait">
            {state === "done" ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex min-h-[420px] flex-col items-start justify-center"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-led">
                  Received
                </p>
                <h3 className="mt-3 font-arch text-2xl font-extrabold text-ink">
                  Thanks — that is logged.
                </h3>
                <p className="mt-4 max-w-[40ch] text-[15px] leading-relaxed text-muted">
                  Someone from the office will be in touch. If you need an
                  answer sooner, call {SITE.phoneLabel}.
                </p>
                <button
                  type="button"
                  onClick={() => setState("idle")}
                  className="mt-8 font-mono text-[10px] uppercase tracking-[0.18em] text-subtle underline-offset-4 transition hover:text-ink hover:underline"
                >
                  Send another
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-5"
                noValidate
              >
                <Field
                  label="Name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  autoComplete="name"
                />
                <Field
                  label="Company"
                  value={form.company}
                  onChange={set("company")}
                  autoComplete="organization"
                />

                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    autoComplete="email"
                  />
                  <Field
                    label="Phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    autoComplete="tel"
                  />
                </div>

                <div>
                  <Label>What do you need</Label>
                  <select
                    value={form.subjectRef}
                    onChange={set("subjectRef")}
                    className="w-full rounded-lg border border-line bg-surface px-4 py-3 text-[15px] text-ink outline-none transition focus:border-brooke-red focus:ring-2 focus:ring-brooke-red/25"
                  >
                    <option value="">Select one</option>
                    {ENQUIRY_SUBJECTS.map((s) => (
                      <option key={s.ref} value={s.ref}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Details</Label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What the equipment is, what it is doing, and where the site is."
                    className="w-full resize-y rounded-lg border border-line bg-surface px-4 py-3 text-[15px] text-ink outline-none transition placeholder:text-subtle focus:border-brooke-red focus:ring-2 focus:ring-brooke-red/25"
                  />
                </div>

                {/* Turnstile mounts here */}
                <div ref={boxRef} className="min-h-[65px]" />

                {error && (
                  <p
                    role="alert"
                    className="rounded-lg border border-brooke-red/40 bg-brooke-red/10 px-4 py-3 text-[13.5px] text-ink"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={state === "sending"}
                  className="inline-flex min-h-[50px] items-center justify-center rounded-full bg-brooke-red px-8 font-mono text-[10px] uppercase tracking-[0.18em] text-white shadow-[0_10px_28px_-10px_rgba(231,0,26,0.8)] transition hover:bg-brooke-redDark focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brooke-red/30 disabled:opacity-50"
                >
                  {state === "sending" ? "Sending" : "Send enquiry"}
                </button>

                <p className="text-[12px] leading-relaxed text-subtle">
                  We use your details to answer this enquiry and keep a record
                  of it. Nothing else.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function Label({ children }) {
  return (
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-subtle">
      {children}
    </span>
  );
}

function Field({ label, required, ...rest }) {
  return (
    <label className="block">
      <Label>
        {label}
        {required && <span className="ml-1 text-brooke-red">*</span>}
      </Label>
      <input
        required={required}
        {...rest}
        className="w-full rounded-lg border border-line bg-surface px-4 py-3 text-[15px] text-ink outline-none transition placeholder:text-subtle focus:border-brooke-red focus:ring-2 focus:ring-brooke-red/25"
      />
    </label>
  );
}